import type { NotificationType } from '../types/notification.types'

/**
 * Clave i18n LITERAL del título de cada tipo de notificación.
 *
 * <p>Las claves no se construyen con `notifications.types.${type}`: una poda
 * de "claves muertas" que busca literales en el código no ve las dinámicas y
 * las borraría (ver CLAUDE.md § claves i18n literales). Al ser un
 * `Record<NotificationType, …>`, TypeScript obliga a declarar aquí la clave
 * de cada tipo nuevo; `notificationTitleKeys.test.ts` verifica que existen en
 * es y en.</p>
 */
export const NOTIFICATION_TITLE_KEYS: Record<NotificationType, string> = {
  RATING_AVAILABLE: 'notifications.types.RATING_AVAILABLE',
  NEW_PARTICIPANT: 'notifications.types.NEW_PARTICIPANT',
  SESSION_REMINDER: 'notifications.types.SESSION_REMINDER',
  SESSION_CANCELLED: 'notifications.types.SESSION_CANCELLED',
  RISKY_PARTICIPANT: 'notifications.types.RISKY_PARTICIPANT',
  FAVORITE_GAME_SESSION: 'notifications.types.FAVORITE_GAME_SESSION',
  NO_SHOW_RECORDED: 'notifications.types.NO_SHOW_RECORDED',
  NO_SHOW_WARNING: 'notifications.types.NO_SHOW_WARNING',
  NO_SHOW_COOLDOWN_ACTIVATED: 'notifications.types.NO_SHOW_COOLDOWN_ACTIVATED',
  RELIABILITY_DECAY: 'notifications.types.RELIABILITY_DECAY',
  WAITLIST_PROMOTED: 'notifications.types.WAITLIST_PROMOTED',
  SESSION_AUTO_CANCELLED_EMPTY: 'notifications.types.SESSION_AUTO_CANCELLED_EMPTY',
  RELIABILITY_RESET_INACTIVITY: 'notifications.types.RELIABILITY_RESET_INACTIVITY',
  STORE_CAMPAIGN_APPROVED: 'notifications.types.STORE_CAMPAIGN_APPROVED',
  STORE_CAMPAIGN_DENIED: 'notifications.types.STORE_CAMPAIGN_DENIED',
  ADMIN_VENUE_PENDING: 'notifications.types.ADMIN_VENUE_PENDING',
  ADMIN_VENUE_EDITED: 'notifications.types.ADMIN_VENUE_EDITED',
  ADMIN_CAMPAIGN_PENDING: 'notifications.types.ADMIN_CAMPAIGN_PENDING',
  ADMIN_EMAIL_QUOTA: 'notifications.types.ADMIN_EMAIL_QUOTA',
  // Solo de respaldo: su título y texto los manda el servidor (ver `usesServerText`).
  COMMUNICATION: 'notifications.types.COMMUNICATION',
  STORE_VENUE_APPROVED: 'notifications.types.STORE_VENUE_APPROVED',
  STORE_VENUE_DENIED: 'notifications.types.STORE_VENUE_DENIED',
  EVENT_NEW_PARTICIPANT: 'notifications.types.EVENT_NEW_PARTICIPANT',
  EVENT_PARTICIPANT_LEFT: 'notifications.types.EVENT_PARTICIPANT_LEFT',
  VENUE_SESSION_CREATED: 'notifications.types.VENUE_SESSION_CREATED',
  VIP_GRANTED: 'notifications.types.VIP_GRANTED',
  NEW_SESSION_NEARBY: 'notifications.types.NEW_SESSION_NEARBY',
  STORE_EVENT_NEARBY: 'notifications.types.STORE_EVENT_NEARBY',
  FOLLOWED_STORE_EVENT: 'notifications.types.FOLLOWED_STORE_EVENT',
  SESSION_EMPTY_WARNING: 'notifications.types.SESSION_EMPTY_WARNING',
  PLAY_INTENT_GROUP: 'notifications.types.PLAY_INTENT_GROUP',
  PLAY_INTENT_SESSION: 'notifications.types.PLAY_INTENT_SESSION',
}

/** Variante de FAVORITE_GAME_SESSION cuando la sesión tiene zona. */
export const FAVORITE_GAME_SESSION_WITH_AREA_KEY =
  'notifications.types.FAVORITE_GAME_SESSION_with_area'

/** Copy genérica para tipos que el BE introdujo antes que el FE. */
export const NOTIFICATION_FALLBACK_KEY = 'notifications.types.fallback'

/**
 * Tipos cuyo texto escribe una persona (Comunicaciones): se pinta el `title`/`body` del
 * servidor, no una plantilla. La clave del mapa queda solo de respaldo.
 */
export function usesServerText(type: string): boolean {
  return type === 'COMMUNICATION'
}

/** Clave del título de `type`, o `null` si el FE aún no conoce el tipo. */
export function notificationTitleKey(type: string): string | null {
  return Object.prototype.hasOwnProperty.call(NOTIFICATION_TITLE_KEYS, type)
    ? NOTIFICATION_TITLE_KEYS[type as NotificationType]
    : null
}
